<template>
    <base-modal
        size="lg"
        hide-header
        hide-footer
        :prevent-close="true"
        @close="handleClose"
    >
        <div style="overflow: hidden">
        <div class="d-flex flex-column editor-form" style="height: 50em; border-radius: .5em">
            <!-- Header -->
            <header class="flex-shrink-0 border-bottom px-2 py-2">
                <h2 class="editor-header mb-0">{{ $t('daily_summary.add_manual_trip_record') }}</h2>
            </header>

            <!-- Scrollable content -->
            <main class="flex-grow-1 overflow-auto px-3 py-2">
                <!-- Info box with invoiceable sum on same row -->
                <div class="d-flex align-items-center justify-content-between gap-3 mb-3">
                    <div class="alert alert-info mb-0" style="width: calc(100% - 20% - 1rem)" role="alert">
                        <i class="fas fa-info-circle me-1"></i>
                        {{ $t('daily_summary.manual_trip_record_info') }}
                    </div>
                    <strong class="text-success text-nowrap" style="font-size: 1.1em; margin-right: 3rem">{{ formatCurrency(invoiceableSum) }}</strong>
                </div>

                <!-- Date -->
                <div class="mb-3">
                    <span class="span-title required">{{ $t('daily_summary.date') }}</span>
                    <input
                        id="date"
                        v-model="record.date"
                        type="date"
                        class="form-control form-control-sm"
                        :class="{'required_field': !record.date}"
                        :max="today"
                    />
                </div>

                <!-- Contract -->
                <div class="mb-3">
                    <open-contract-filter
                        :from="record.date"
                        :show-company="false"
                        :show-title="true"
                        :contract="record.contract"
                        :show-only-my-contracts-option="true"
                        :disabled="!record.date"
                        @contractSelected="onContractSelected"
                    />
                    <small v-if="!record.date" class="text-muted">
                        {{ $t('daily_summary.select_date_first') }}
                    </small>
                </div>

                <!-- Order -->
                <div class="mb-3">
                    <contract-order-multiselect
                        ref="orderFilter"
                        :contract-ids="record.contract ? [record.contract.id] : []"
                        :single-mode="true"
                        :pre-selected-order="record.order ? record.order.id : null"
                        :open-or-planned-only="false"
                        :show-open-or-planned-option="true"
                        :disabled="!record.contract"
                        @orders-selected="setOrder"
                    />
                    <small v-if="!record.contract" class="text-muted">
                        {{ $t('daily_summary.select_contract_first') }}
                    </small>
                </div>

                <!-- Vehicle -->
                <div class="mb-3">
                    <span class="span-title required">{{ $t('trip_list.vehicle') }}</span>
                    <vehicle-selector-dropdown
                        :vehicle-id="record.vehicle ? record.vehicle.id : null"
                        @onVehicleSelected="setVehicle"
                    />
                </div>

                <!-- Unit Price, Pricing Model and Invoiceable Amount on same row -->
                <div class="row mb-3">
                    <div class="col-sm-4">
                        <span class="span-title required">{{ $t('trip_list.unit_price') }}</span>
                        <div class="input-group input-group-sm">
                            <input
                                id="unitPrice"
                                v-model.number="record.unitPrice"
                                type="number"
                                step="0.001"
                                min="0"
                                class="form-control form-control-sm"
                                :class="{'required_field': record.unitPrice === null || record.unitPrice === undefined}"
                            />
                            <span class="input-group-text">{{ currency }}</span>
                        </div>
                    </div>
                    <div class="col-sm-4">
                        <span class="span-title required">{{ $t('unit.select_unit') }}</span>
                        <select
                            id="pricingModel"
                            v-model="record.pricingModel"
                            class="form-select form-select-sm"
                            :class="{'required_field': !record.pricingModel}"
                        >
                            <option :value="null" disabled>
                                {{ $t('unit.select_unit') }}
                            </option>
                            <option
                                v-for="option in pricingModels"
                                :key="option.id"
                                :value="option"
                            >
                                {{ currency }} / {{ unitToText(option.unit1) }}
                                {{ option.unit2 ? "/" + unitToText(option.unit2) : "" }}
                            </option>
                        </select>
                    </div>
                    <div class="col-sm-4">
                        <span class="span-title required">{{ $t('trip_list.amount_to_invoice') }}</span>
                        <input
                            id="invoiceableAmount"
                            v-model.number="record.invoiceableAmount"
                            type="number"
                            step="0.01"
                            min="0"
                            class="form-control form-control-sm"
                            :class="{'required_field': record.invoiceableAmount === null || record.invoiceableAmount === undefined}"
                        />
                    </div>
                </div>

                <!-- Explanation -->
                <div class="mb-3">
                    <span class="span-title required">{{ $t('daily_summary.new_record_explanation') }}</span>
                    <textarea
                        id="explanation"
                        v-model="record.explanation"
                        class="form-control form-control-sm"
                        :class="{'required_field': !record.explanation || record.explanation.length < 5}"
                        rows="3"
                    ></textarea>
                    <small v-if="record.explanation && record.explanation.length < 5" class="text-danger">
                        {{ $t('common.min_chars', { count: 5 }) }}
                    </small>
                </div>
            </main>

            <!-- Footer -->
            <footer class="flex-shrink-0 border-top px-3 py-2">
                <div class="d-flex justify-content-end gap-2">
                    <button
                        class="btn btn-sm btn-secondary"
                        @click="handleClose"
                    >
                        {{ $t('common.cancel') }}
                    </button>
                    <button
                        class="btn btn-sm btn-success"
                        :disabled="!isFormValid || saving"
                        @click="saveRecord"
                    >
                        <span v-if="saving">
                            <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            {{ $t('common.saving') }}
                        </span>
                        <span v-else>
                            {{ $t('common.save') }}
                        </span>
                    </button>
                </div>
            </footer>
        </div>
        </div>
    </base-modal>
</template>

<script>
import BaseModal from "@/components/BaseModal";
import ContractOrderMultiselect from "@/components/order/ContractOrderMultiselect";
import VehicleSelectorDropdown from "@/components/vehicle/VehicleSelectorDropdown";
import OpenContractFilter from "@/components/contract/OpenContractFilter";
import {restApi} from "@/components/mixins/RestApiMixin";
import {priceHelper} from "@/components/mixins/PricingMixin";
import {timeUtils} from "@/components/mixins/TimeUtils";
import {inject} from "vue";

export default {
    name: "ManualTripRecordEditor",
    components: {
        BaseModal,
        ContractOrderMultiselect,
        VehicleSelectorDropdown,
        OpenContractFilter
    },
    mixins: [restApi, priceHelper, timeUtils],
    props: {
        user: {
            type: Object,
            required: true
        },
        searchParams: {
            type: Object,
            default: null
        }
    },
    setup() {
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data() {
        return {
            record: {
                date: null,
                contract: null,
                order: null,
                vehicle: null,
                unitPrice: null,
                pricingModel: null,
                invoiceableAmount: null,
                explanation: ''
            },
            pricingModels: [],
            saving: false,
            today: this.timestampToDateInput(new Date().getTime())
        }
    },
    computed: {
        currency() {
            return import.meta.env.VITE_APP_CURRENCY || '€'
        },
        invoiceableSum() {
            if (this.record.unitPrice !== null &&
                this.record.unitPrice !== undefined &&
                this.record.invoiceableAmount !== null &&
                this.record.invoiceableAmount !== undefined) {
                return this.record.unitPrice * this.record.invoiceableAmount;
            }
            return 0;
        },
        isFormValid() {
            return !!(
                this.record.date &&
                this.record.contract &&
                this.record.order &&
                this.record.vehicle &&
                this.record.unitPrice !== null &&
                this.record.unitPrice !== undefined &&
                this.record.unitPrice !== '' &&
                !isNaN(this.record.unitPrice) &&
                this.record.pricingModel &&
                this.record.invoiceableAmount !== null &&
                this.record.invoiceableAmount !== undefined &&
                this.record.invoiceableAmount !== '' &&
                !isNaN(this.record.invoiceableAmount) &&
                this.record.explanation &&
                this.record.explanation.length >= 5
            );
        }
    },
    mounted() {
        this.fetchPricingModels();
    },
    methods: {
        onContractSelected(contract) {
            if (contract && (!this.record.contract || this.record.contract.id !== contract.id)) {
                this.record.contract = contract;
                this.record.order = null;
                this.record.invoiceableAmount = null;
            } else if (!contract) {
                this.record.contract = null;
                this.record.order = null;
            }
        },
        setOrder(orders) {
            // In singleMode the component emits a single object, not an array
            const order = Array.isArray(orders) ? orders[0] : orders;
            if (order && order.id) {
                this.record.order = order;
                // Always auto-fill unit price and pricing model from the selected order
                if (order.unit_price) {
                    this.record.unitPrice = order.unit_price / 100; // Convert from cents
                } else {
                    this.record.unitPrice = null;
                }
                if (order.pricing_model) {
                    // Try to match against the full pricingModels list for the select dropdown
                    const match = this.pricingModels.find(p => p.id === order.pricing_model.id);
                    this.record.pricingModel = match || order.pricing_model;
                } else {
                    this.record.pricingModel = null;
                }
            } else if (!order || (Array.isArray(orders) && orders.length === 0)) {
                // Only clear if explicitly deselected, not on initial empty load
                if (this.record.order) {
                    this.record.order = null;
                    this.record.unitPrice = null;
                    this.record.pricingModel = null;
                }
            }
        },
        setVehicle(vehicle) {
            this.record.vehicle = vehicle;
        },
        fetchPricingModels() {
            this.restFetch(this.pricingModeUrl, this.handlePricingModels);
        },
        handlePricingModels(response) {
            if (response && response.data) {
                this.pricingModels = response.data;
            }
        },
        formatCurrency(value) {
            if (value === null || value === undefined) return '-';
            return new Intl.NumberFormat('fi-FI', {
                style: 'currency',
                currency: 'EUR'
            }).format(value);
        },
        handleClose() {
            this.$emit('close');
        },
        saveRecord() {
            if (!this.isFormValid) {
                if (this.showAlert) this.showAlert(this.$t('common.fill_required_fields'), 'warning');
                return;
            }

            this.saving = true;

            // Prepare the payload according to backend requirements
            const payload = {
                date: this.record.date,
                vehicle: { id: this.record.vehicle.id },
                driver: this.user.id,
                contract: { id: this.record.contract.id },
                work_order: { id: this.record.order.id },
                unit_price: this.record.unitPrice,
                pricing_model: { id: this.record.pricingModel.id },
                invoiceable_amount: this.record.invoiceableAmount,
                invoiceable_sum: this.invoiceableSum,
                invoice_explanation: this.record.explanation,
            };

            // Use the daily trip summary URL to create the manual record
            this.restAdd(
                this.contractDailyTripSummaryManualUrl,
                payload,
                this.saveSuccess,
                this.saveFail
            );
        },
        saveSuccess(response) {
            this.saving = false;
            if (this.showAlert) this.showAlert(this.$t('daily_summary.manual_trip_record_added'), 'success');
            this.$emit('saved', response);
            this.$emit('close');
        },
        saveFail() {
            this.saving = false;
            if (this.showAlert) this.showAlert(this.$t('common.alert_update_failed'), 'error');
        }
    }
}
</script>

<style scoped>
.required_field {
    border-color: #dc3545 !important;
}

.span-title.required::after {
    content: " *";
    color: #dc3545;
}

.gap-2 {
    gap: 0.5rem;
}
</style>
